import React, {
  useCallback,
  useEffect,
  useState
} from 'react';

import {
  Bell,
  CheckCircle
} from 'lucide-react';

const API = (
  process.env.REACT_APP_API_URL ||
  '/api'
).replace(/\/$/, '');

function urlBase64ToUint8Array(
  base64String
) {
  const padding =
    '='.repeat(
      (
        4 -
        base64String.length % 4
      ) % 4
    );

  const base64 = (
    base64String + padding
  )
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData =
    window.atob(base64);

  return Uint8Array.from(
    [...rawData].map(
      char =>
        char.charCodeAt(0)
    )
  );
}

export default function PushNotificationsCard({
  darkMode
}) {
  const card =
    darkMode
      ? 'bg-gray-800'
      : 'bg-white';

  const border =
    darkMode
      ? 'border-gray-700'
      : 'border-gray-200';

  const text =
    darkMode
      ? 'text-white'
      : 'text-gray-900';

  const muted =
    darkMode
      ? 'text-gray-400'
      : 'text-gray-600';

  const [
    status,
    setStatus
  ] = useState({
    supported: null,
    permission: 'default',
    activeSubscriptions: 0
  });

  const [
    busy,
    setBusy
  ] = useState('');

  const [
    message,
    setMessage
  ] = useState('');

  const carregarStatus =
    useCallback(async () => {
      const supported =
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window;

      if (!supported) {
        setStatus({
          supported: false,
          permission: 'unsupported',
          activeSubscriptions: 0
        });

        return;
      }

      try {
        const response =
          await fetch(
            `${API}/push/status`,
            {
              credentials: 'include'
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          data.success === false
        ) {
          throw new Error(
            data.error ||
            'Falha ao consultar notificações.'
          );
        }

        setStatus({
          supported: true,
          permission:
            Notification.permission,
          activeSubscriptions:
            Number(
              data.activeSubscriptions ||
              0
            )
        });
      } catch (error) {
        setStatus(current => ({
          ...current,
          supported: true,
          permission:
            Notification.permission,
          error:
            error.message
        }));
      }
    }, []);

  useEffect(() => {
    carregarStatus();
  }, [carregarStatus]);

  const ativar =
    async () => {
      setMessage('');
      setBusy('activate');

      try {
        const supported =
          'serviceWorker' in navigator &&
          'PushManager' in window &&
          'Notification' in window;

        if (!supported) {
          throw new Error(
            'Este navegador não oferece suporte a notificações Push.'
          );
        }

        const isIOS =
          /iphone|ipad|ipod/i.test(
            navigator.userAgent
          );

        const isStandalone =
          (
            window.matchMedia &&
            window.matchMedia(
              '(display-mode: standalone)'
            ).matches
          ) ||
          window.navigator
            .standalone === true;

        if (
          isIOS &&
          !isStandalone
        ) {
          throw new Error(
            'No iPhone, adicione primeiro o CRM à Tela de Início pelo Safari e abra pelo ícone FINANCY.'
          );
        }

        let permission =
          Notification.permission;

        if (
          permission === 'default'
        ) {
          permission =
            await Notification
              .requestPermission();
        }

        if (
          permission !== 'granted'
        ) {
          throw new Error(
            'A permissão para notificações não foi concedida.'
          );
        }

        const registration =
          await navigator
            .serviceWorker
            .ready;

        let subscription =
          await registration
            .pushManager
            .getSubscription();

        if (!subscription) {
          const keyResponse =
            await fetch(
              `${API}/push/public-key`,
              {
                credentials: 'include'
              }
            );

          const keyData =
            await keyResponse.json();

          if (
            !keyResponse.ok ||
            !keyData.publicKey
          ) {
            throw new Error(
              keyData.error ||
              'Não foi possível obter a chave Push.'
            );
          }

          subscription =
            await registration
              .pushManager
              .subscribe({
                userVisibleOnly: true,
                applicationServerKey:
                  urlBase64ToUint8Array(
                    keyData.publicKey
                  )
              });
        }

        const response =
          await fetch(
            `${API}/push/subscribe`,
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type':
                  'application/json'
              },
              body: JSON.stringify({
                subscription:
                  subscription.toJSON
                    ? subscription.toJSON()
                    : subscription,
                deviceLabel:
                  isIOS
                    ? 'iPhone / iPad'
                    : 'Navegador'
              })
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          data.success === false
        ) {
          throw new Error(
            data.error ||
            'Falha ao registrar este dispositivo.'
          );
        }

        setMessage(
          'Notificações ativadas neste dispositivo.'
        );

        await carregarStatus();
      } catch (error) {
        setMessage(
          error.message ||
          'Não foi possível ativar as notificações.'
        );
      } finally {
        setBusy('');
      }
    };

  const enviarTeste =
    async () => {
      setMessage('');
      setBusy('test');

      try {
        const response =
          await fetch(
            `${API}/push/test`,
            {
              method: 'POST',
              credentials: 'include'
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          data.success === false
        ) {
          throw new Error(
            data.error ||
            'Nenhuma notificação foi enviada.'
          );
        }

        setMessage(
          `Teste enviado para ${data.sent} dispositivo(s).`
        );
      } catch (error) {
        setMessage(
          error.message ||
          'Falha ao enviar notificação de teste.'
        );
      } finally {
        setBusy('');
      }
    };

  return (
    <section
      className={
        `${card} ${border} border rounded-xl p-6`
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="bg-blue-100 text-blue-700 p-2 rounded-lg">
            <Bell size={22} />
          </div>

          <div>
            <h2
              className={
                `${text} text-lg font-semibold`
              }
            >
              Notificações no celular
            </h2>

            <p
              className={
                `${muted} text-sm mt-1`
              }
            >
              Receba avisos do CRM mesmo quando o painel estiver fechado.
            </p>

            <div
              className={
                `${muted} text-xs mt-3 space-y-1`
              }
            >
              <div>
                Suporte:{' '}
                {
                  status.supported === false
                    ? 'Não disponível'
                    : status.supported === true
                      ? 'Disponível'
                      : 'Verificando...'
                }
              </div>

              <div>
                Permissão:{' '}
                {
                  status.permission ||
                  'default'
                }
              </div>

              <div>
                Dispositivos ativos:{' '}
                {
                  status.activeSubscriptions ||
                  0
                }
              </div>
            </div>

            <p
              className={
                `${muted} text-xs mt-3 max-w-2xl`
              }
            >
              No iPhone: Safari → Compartilhar → Adicionar à Tela de Início. Depois abra pelo ícone FINANCY e ative as notificações.
            </p>

            {message && (
              <div
                className={
                  `${text} text-sm mt-3 font-medium`
                }
              >
                {message}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={ativar}
            disabled={Boolean(busy)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50"
          >
            <Bell size={17} />

            {
              busy === 'activate'
                ? 'Ativando...'
                : 'Ativar neste dispositivo'
            }
          </button>

          <button
            type="button"
            onClick={enviarTeste}
            disabled={
              Boolean(busy) ||
              !status.activeSubscriptions
            }
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-50"
          >
            <CheckCircle size={17} />

            {
              busy === 'test'
                ? 'Enviando...'
                : 'Enviar teste'
            }
          </button>
        </div>
      </div>
    </section>
  );
}
